import {
  ISeriesPrimitive,
  SeriesAttachedParameter,
  Time,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  PrimitivePaneViewZOrder,
} from 'lightweight-charts';
import {
  LAYOUT_PRIORITY,
  MarkerLayoutClient,
  type LayoutContext,
  type LayoutItem,
  type MarkerLayoutRegistry,
} from './markerLayout';

export interface MagnetLevel {
  timestamp: number;        // ms - origin candle
  price: number;            // magnet level price
  direction: 'bullish' | 'bearish';
  valid: boolean;
  invalidatedAt: number | null;  // ms - when invalidated
  range?: number;           // candle range (high - low) in price points
}

interface MagnetLevelRendererData {
  x: number;             // logical X of origin candle
  y: number;             // logical Y for marker (offset from candle)
  isBullish: boolean;
  dim: boolean;
  range?: number;        // candle range to show in brackets
}

interface ExtensionLineData {
  startX: number;        // logical X where the line starts (origin candle)
  priceY: number;        // logical Y at the magnet price (not offset)
  isBullish: boolean;
  chartWidth: number;    // right edge in logical coords
}

const EMOJI_SIZE = 9; // px before scaling (smaller)
/** Shared layout: the 🧲's half-height and the (range) label's centre distance from it. */
const GLYPH_HALF = 7;
const LABEL_STEP = 11;

class MagnetCandlePaneRenderer implements IPrimitivePaneRenderer {
  private _data: MagnetLevelRendererData[] = [];
  private _extensionLines: ExtensionLineData[] = [];

  update(data: MagnetLevelRendererData[], extensionLines: ExtensionLineData[]): void {
    this._data = data;
    this._extensionLines = extensionLines;
  }

  draw(target: any): void {
    target.useBitmapCoordinateSpace((scope: any) => {
      const ctx = scope.context as CanvasRenderingContext2D;
      const ratio = scope.horizontalPixelRatio as number;
      const vRatio = scope.verticalPixelRatio as number;
      const fontSize = EMOJI_SIZE * ratio;
      ctx.font = `${fontSize}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#ffffff';

      for (const d of this._data) {
        const x = d.x * ratio;
        const y = d.y * vRatio;
        ctx.globalAlpha = d.dim ? 0.2 : 1.0;

        ctx.fillText('\u{1F9F2}', x, y);

        if (d.range && d.range > 0) {
          const textOffset = (d.isBullish ? -11 : 11) * vRatio;
          ctx.fillText(`(${d.range.toFixed(1)})`, x, y + textOffset);
        }
      }
      ctx.globalAlpha = 1.0;

      // --- Extension lines: dashed horizontal from origin candle to right edge ---
      for (const line of this._extensionLines) {
        const lx = line.startX * ratio;
        const ly = line.priceY * vRatio;
        const rx = line.chartWidth * ratio;

        ctx.save();
        ctx.globalAlpha = 0.45;
        ctx.strokeStyle = line.isBullish ? '#22c55e' : '#ef4444'; // green / red
        ctx.lineWidth = 1 * ratio;
        ctx.setLineDash([4 * ratio, 4 * ratio]);
        ctx.beginPath();
        ctx.moveTo(lx, ly);
        ctx.lineTo(rx, ly);
        ctx.stroke();
        ctx.restore();
      }
    });
  }
}

class MagnetCandlePaneView implements IPrimitivePaneView {
  private _source: MagnetCandlePrimitive;
  private _renderer: MagnetCandlePaneRenderer;

  constructor(source: MagnetCandlePrimitive) {
    this._source = source;
    this._renderer = new MagnetCandlePaneRenderer();
  }

  zOrder(): PrimitivePaneViewZOrder {
    return 'top';
  }

  renderer(): IPrimitivePaneRenderer {
    const { markers, extensionLines } = this._source.getRendererData();
    this._renderer.update(markers, extensionLines);
    return this._renderer;
  }
}

export interface CandleData {
  timestamp: number;  // ms
  high: number;
  low: number;
}

export class MagnetCandlePrimitive implements ISeriesPrimitive<Time> {
  private _levels: MagnetLevel[] = [];
  private _candles: CandleData[] = [];
  private _extensionLineCount = 5;
  private _paneView: MagnetCandlePaneView;
  private _requestUpdate?: () => void;
  private _series?: SeriesAttachedParameter<Time>['series'];
  private _chart?: SeriesAttachedParameter<Time>['chart'];
  private _layout = new MarkerLayoutClient('magnet', (ctx) => this._layoutItems(ctx));

  constructor() {
    this._paneView = new MagnetCandlePaneView(this);
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this._series = param.series;
    this._chart = param.chart;
    this._requestUpdate = param.requestUpdate;
    this._layout.setAttached(true);
  }

  detached(): void {
    this._layout.setAttached(false);
    this._series = undefined;
    this._chart = undefined;
    this._requestUpdate = undefined;
  }

  /** Join the chart's shared marker layout (null: the fixed lane alone). */
  setMarkerLayout(reg: MarkerLayoutRegistry | null): void {
    this._layout.setRegistry(reg);
  }

  /** One box per magnet: the 🧲 near the candle, its (range) label beyond. */
  private _layoutItems(ctx: LayoutContext): LayoutItem[] {
    const out: LayoutItem[] = [];
    for (const level of this._levels) {
      const t = Math.floor(level.timestamp / 1000);
      if (t < ctx.fromTime || t > ctx.toTime) continue;
      const x = ctx.timeToX(t);
      const y = ctx.priceToY(level.price);
      if (x === null || y === null) continue;
      const label = level.range && level.range > 0 ? `(${level.range.toFixed(1)})` : '';
      const w = Math.max(GLYPH_HALF * 2, label ? ctx.measure(label, `${EMOJI_SIZE}px Arial`) : 0) + 2;
      const h = GLYPH_HALF * 2 + (label ? LABEL_STEP : 0);
      const bull = level.direction === 'bullish';
      out.push({
        key: `${level.timestamp}:${level.direction}:${level.price}`,
        time: t,
        x,
        // Bullish magnet price = candle HIGH, marker above; bearish below the LOW.
        side: bull ? 'above' : 'below',
        anchorY: y,
        w: Math.ceil(w),
        h,
        gap: 3,
        priority: LAYOUT_PRIORITY.magnet,
        color: bull ? '#22c55e' : '#ef4444',
        label: `🧲 magnet ${level.price}${label ? ` ${label}` : ''}`,
        ref: level,
      });
    }
    return out;
  }

  paneViews(): IPrimitivePaneView[] {
    return [this._paneView];
  }

  updateAllViews(): void {
    // Called when chart needs to update views
  }

  setLevels(levels: MagnetLevel[]): void {
    this._levels = levels;
    this._layout.invalidate();
    this._requestUpdate?.();
  }

  /** Supply candle high/low data for client-side breach detection. */
  setCandles(candles: CandleData[]): void {
    this._candles = candles;
    // No requestUpdate needed — positions recalculate on next frame anyway.
  }

  setExtensionLineCount(count: number): void {
    this._extensionLineCount = count;
    this._requestUpdate?.();
  }

  /**
   * Client-side breach check: has any candle after the magnet's timestamp
   * traded through the magnet price?
   *   - Bullish magnet (price = candle high) is breached if a later candle's high >= price
   *   - Bearish magnet (price = candle low) is breached if a later candle's low <= price
   */
  private _isBreached(level: MagnetLevel): boolean {
    for (let i = this._candles.length - 1; i >= 0; i--) {
      const c = this._candles[i];
      if (c.timestamp <= level.timestamp) break; // only check candles after origin
      if (level.direction === 'bullish' && c.high >= level.price) return true;
      if (level.direction === 'bearish' && c.low <= level.price) return true;
    }
    return false;
  }

  getRendererData(): { markers: MagnetLevelRendererData[]; extensionLines: ExtensionLineData[] } {
    if (!this._series || !this._chart) return { markers: [], extensionLines: [] };

    const timeScale = this._chart.timeScale();
    const markers: MagnetLevelRendererData[] = [];
    const extensionLines: ExtensionLineData[] = [];

    // Chart right edge in logical coords (for extension line end point)
    const chartWidth = this._chart.timeScale().width();

    // Collect unbreached levels for extension lines (most recent first)
    const unbreachedForLines: { level: MagnetLevel; x: number; priceY: number }[] = [];

    // Shared layout: the glyph sits where its box landed (the line stays on the price).
    const shared = this._layout.placed();
    const glyphY = new Map<MagnetLevel, number>();
    if (shared) {
      for (const p of shared) {
        const level = p.item.ref as MagnetLevel;
        glyphY.set(level, level.direction === 'bullish' ? p.top + p.h - GLYPH_HALF : p.top + GLYPH_HALF);
      }
    }

    for (const level of this._levels) {
      const priceY = this._series.priceToCoordinate(level.price);
      if (priceY === null) continue;

      const originTime = Math.floor(level.timestamp / 1000) as Time;
      const x = timeScale.timeToCoordinate(originTime);
      if (x === null) continue;

      const isBullish = level.direction === 'bullish';
      // Bullish magnet price = candle HIGH, marker goes ABOVE (negative Y = up on screen)
      // Bearish magnet price = candle LOW, marker goes BELOW (positive Y = down on screen)
      // Offset 28px to clear WCC markers (which sit at 12px with 7px ring radius)
      const offset = isBullish ? -28 : 28;

      const placedY = glyphY.get(level);
      if (shared && placedY === undefined) {
        // Folded into a "+N" chip: no glyph, but its line still draws.
        if (level.valid && this._extensionLineCount > 0 && !this._isBreached(level)) unbreachedForLines.push({ level, x, priceY });
        continue;
      }
      markers.push({
        x,
        y: placedY ?? priceY + offset,
        isBullish,
        dim: !level.valid,
        range: level.range,
      });

      // Candidate for extension line: valid + not breached client-side
      if (level.valid && this._extensionLineCount > 0 && !this._isBreached(level)) {
        unbreachedForLines.push({ level, x, priceY });
      }
    }

    // Take the N most recent unbreached levels (levels are sorted newest-first from backend)
    const lineCandidates = unbreachedForLines.slice(0, this._extensionLineCount);
    for (const { level, x, priceY } of lineCandidates) {
      extensionLines.push({
        startX: x,
        priceY,
        isBullish: level.direction === 'bullish',
        chartWidth,
      });
    }

    return { markers, extensionLines };
  }
}
